import React from "react";
import {useAppDispatch, useAppSelector} from "../../../../app/store";
import {QuestionOrderOption} from "../../../../types/admin";
import {v4 as uuidv4} from "uuid";
import {ADMIN_LANG, AVAILABLE_LANGUAGES} from "../../../../settings";
import {changeQuestionModal} from "../../../../app/slices/modals/questionEditModal";
import {DragDropContext, Draggable, Droppable, DropResult} from "@hello-pangea/dnd";
import {GripVertical, PlusCircle, Trash} from "react-bootstrap-icons";
import {Col, Form, Row} from "react-bootstrap";

type Props = {
    language: string;
    handlePointsChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    is_draft: boolean;
};

const QuestionEditOrder = ({language, handlePointsChange, is_draft}: Props) => {
    const {order_options, points} = useAppSelector((state) => state.questionEditModal);
    const dispatch = useAppDispatch();

    const handleAddOption = () => {
        if (!is_draft) return;
        const newOption: QuestionOrderOption = {
            option_id: uuidv4(),
            key: uuidv4(),
            languages: AVAILABLE_LANGUAGES.map((lang) => ({
                language_code: lang,
                option: '',
            })),
        };
        dispatch(changeQuestionModal({order_options: [...order_options, newOption]}));
    };

    const handleRemoveOption = (id: string) => {
        if (!is_draft) return;
        dispatch(changeQuestionModal({order_options: order_options.filter((option) => option.option_id !== id)}));
    };

    const handleOptionTitleChange = (id: string, lang: string, title: string) => {
        const updatedOptions = order_options.map((option) => {
            if (option.option_id === id) {
                return {
                    ...option,
                    languages: option.languages.map((languageOption) =>
                        languageOption.language_code === lang
                            ? {...languageOption, option: title}
                            : languageOption
                    ),
                };
            }
            return option;
        });
        dispatch(changeQuestionModal({order_options: updatedOptions}));
    };

    const handleDragEnd = (result: DropResult) => {
        if (!is_draft) return;
        if (!result.destination) return;
        const reorderedOptions = [...order_options];
        const [removed] = reorderedOptions.splice(result.source.index, 1);
        reorderedOptions.splice(result.destination.index, 0, removed);
        dispatch(changeQuestionModal({order_options: reorderedOptions}));
    };

    return (
        <Col xs={12}>
            <Form.Group className='pb-3' controlId={`points_${language}`}>
                <Form.Label>{ADMIN_LANG.points}</Form.Label>
                <Form.Control
                    type='number'
                    onChange={handlePointsChange}
                    value={points}
                    disabled={!is_draft}
                />
            </Form.Group>
            <h4 className='pb-2 pt-3'>
                {ADMIN_LANG.order_options}{' '}
                <PlusCircle
                    onClick={handleAddOption}
                    className={`utility-icon ${is_draft ? "green" : "disabled"}`}
                />
            </h4>
            <Row>
                <Col xs={12}>
                    <Form.Label>
                        {`${ADMIN_LANG.title} (${language.toUpperCase()})`}
                    </Form.Label>
                </Col>
            </Row>
            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="droppable">
                    {(provided, snapshot) => (
                        <Row
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            className={`gy-2 ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
                        >
                            {order_options.map((option, index) => {
                                const languageOption = option.languages.find(
                                    (lang) => lang.language_code === language
                                );

                                // Generate the help text
                                const optionHelpText = option.languages
                                    .filter(
                                        (lang) =>
                                            lang.language_code !== language && lang.option
                                    )
                                    .map((lang) => (
                                        <div key={lang.language_code}>
                                            <strong>{lang.language_code.toUpperCase()}: </strong>
                                            {lang.option}
                                        </div>
                                    ));

                                return (
                                    <Draggable isDragDisabled={!is_draft} key={option.key} draggableId={option.key}
                                               index={index}>
                                        {(provided, snapshot) => (
                                            <Col
                                                xs={12}
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                className={`${snapshot.isDragging ? 'dragging' : ''}`}
                                            >
                                                <Row className='row'>
                                                    <Col xs={10}>
                                                        <Form.Group controlId={`option_${option.key}_${language}`}>
                                                            <Form.Control
                                                                type='text'
                                                                maxLength={255}
                                                                className={`form-control ${!is_draft ? 'disabled' : ''}`}
                                                                value={languageOption?.option || ''}
                                                                onChange={(e) =>
                                                                    handleOptionTitleChange(option.option_id, language, e.target.value)
                                                                }
                                                            />
                                                            {optionHelpText.length > 0 &&
                                                                <Form.Text className='help-text'>
                                                                    {optionHelpText}
                                                                </Form.Text>
                                                            }
                                                        </Form.Group>
                                                    </Col>
                                                    <Col xs={2}
                                                         className='d-flex justify-content-center align-items-center'>
                                                        <GripVertical
                                                            className={`utility-icon drag-icon ${!is_draft ? 'disabled' : ''}`}
                                                        />
                                                        <Trash
                                                            onClick={() => handleRemoveOption(option.option_id)}
                                                            className={`utility-icon ${!is_draft ? 'disabled' : 'red'}`}
                                                        />
                                                    </Col>
                                                </Row>
                                            </Col>
                                        )}
                                    </Draggable>
                                );
                            })}
                            {provided.placeholder}
                        </Row>
                    )}
                </Droppable>
            </DragDropContext>
        </Col>
    );
};

export default QuestionEditOrder;